import emailjs from '@emailjs/browser';

import {
  Box,
  BreadcrumbCurrentLink,
  BreadcrumbRoot,
  Button,
  CardBody,
  CardRoot,
  Flex,
  PageHeader,
  PageTitle,
  Status,
  Text,
  toaster,
  VStack,
} from '@oaknorth/component-core-v2';
import { Form, FormApi, InputField, makeValidate, TextareaField, yup } from '@oaknorth/component-forms';
import { T, useTranslation } from '@oaknorth/util-intl';
import { ResponsivePageContainer } from '@oaknorth/util-responsive';
import { Helmet } from '@oaknorth/util-routing';
import { getLogger } from '@oaknorth/util-rum';

import { getEmailJSEnvs } from './getEmailJSEnvs';

type FormValues = {
  name: string;
  email: string;
  subject: string;
  description: string;
};

const validateForm = makeValidate(
  yup.object({
    name: yup.string().required(),
    email: yup.string().email().required(),
    subject: yup.string().required(),
    description: yup.string().required(),
  })
);

export function Support() {
  const { t } = useTranslation();
  const title = t`support.title`;

  const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    const { serviceId, templateId, publicKey } = getEmailJSEnvs();

    if (!serviceId || !templateId || !publicKey) {
      const logger = getLogger();

      logger.exception(new Error('EmailJS environment variables not set'));

      toaster.create({
        title: t`support.form.error`,
        type: Status.ERROR,
      });

      return;
    }

    try {
      await emailjs.send(serviceId, templateId, values, {
        publicKey,
      });

      toaster.create({
        title: t`support.form.success`,
        type: Status.SUCCESS,
      });

      form.restart();
    } catch (e) {
      toaster.create({
        title: t`support.form.error`,
        type: Status.ERROR,
      });
    }
  };

  return (
    <ResponsivePageContainer
      nav={
        <BreadcrumbRoot>
          <BreadcrumbCurrentLink isHomePage>{title}</BreadcrumbCurrentLink>
        </BreadcrumbRoot>
      }
    >
      <PageHeader>
        <Helmet title={title} />
      </PageHeader>
      <Flex direction="column" width="md" margin="0 auto" gap={8}>
        <Box>
          <PageTitle>{title}</PageTitle>
          <Text width="60%">{T`support.description`}</Text>
        </Box>
        <Box background="white" boxShadow="xs">
          <Form<FormValues> onSubmit={onSubmit} validate={validateForm}>
            {({ handleSubmit }) => {
              return (
                <CardRoot>
                  <CardBody>
                    <VStack gap={8} as="form" onSubmit={handleSubmit}>
                      <InputField size="sm" fieldName="name" labelText={T`support.form.name.label`} />
                      <InputField size="sm" fieldName="email" labelText={T`support.form.email.label`} />
                      <InputField size="sm" fieldName="subject" labelText={T`support.form.subject.label`} />
                      <TextareaField
                        minHeight="100px"
                        fieldName="description"
                        labelText={T`support.form.description.label`}
                      />
                      <Flex width="100%" justifyContent="end">
                        <Button variant="solid" type="submit">
                          {T`support.form.submit`}
                        </Button>
                      </Flex>
                    </VStack>
                  </CardBody>
                </CardRoot>
              );
            }}
          </Form>
        </Box>
      </Flex>
    </ResponsivePageContainer>
  );
}
